import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Check, ExternalLink } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  IconButton,
  Spinner,
} from '@elbklar/ui';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface ScheduleLink {
  id: string;
  slug: string;
  display_name: string;
  public_url: string;
  is_active: boolean;
}

interface Props {
  customerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Link, mit dem der Kunde selbst einen Besichtigungstermin bucht. Geöffnet
 * über „Besichtigung planen“ im Kopf der Kundenseite (am Handy im Menü „…“);
 * gibt es noch keinen Link, wird er beim Öffnen angelegt.
 */
export function ScheduleLinkDialog({ customerId, open, onOpenChange }: Props) {
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  // Gibt es schon einen aktiven Link?
  const { data: existingLink, isLoading } = useQuery<ScheduleLink | null>({
    queryKey: ['schedule-link', customerId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/customers/${customerId}/schedule-link`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: open,
  });

  const createMutation = useMutation<ScheduleLink>({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/customers/${customerId}/schedule-link`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_duration: 60 }),
      });
      if (!res.ok) throw new Error('Link ließ sich nicht anlegen');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule-link', customerId] });
    },
  });

  const activeLink = existingLink || createMutation.data;
  const { mutate, isPending, isError, isIdle } = createMutation;

  useEffect(() => {
    if (open && !isLoading && !existingLink && isIdle) mutate();
  }, [open, isLoading, existingLink, isIdle, mutate]);

  const copyUrl = () => {
    if (activeLink) {
      navigator.clipboard.writeText(activeLink.public_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Besichtigung planen</DialogTitle>
          <DialogDescription>
            Mit diesem Link bucht der Kunde selbst einen Besichtigungstermin.
          </DialogDescription>
        </DialogHeader>
        {activeLink ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted py-1.5 pl-3 pr-1.5">
              <input
                type="text"
                readOnly
                aria-label="Link zur Terminbuchung"
                value={activeLink.public_url ?? ''}
                className="min-w-0 flex-1 bg-transparent font-mono text-field text-foreground outline-none"
              />
              <IconButton icon={copied ? Check : Copy} label="Link kopieren" variant="ghost" size="sm" onClick={copyUrl} />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="primary" className="flex-1" icon={copied ? Check : Copy} onClick={copyUrl}>
                {copied ? 'Kopiert' : 'Link kopieren'}
              </Button>
              <Button icon={ExternalLink} onClick={() => window.open(activeLink.public_url, '_blank')}>
                Öffnen
              </Button>
            </div>

            <p className="t-small text-muted-foreground">
              Der Link rechnet die Anfahrt ein und berücksichtigt die Verfügbarkeit aller Teilnehmer.
            </p>
          </div>
        ) : isError ? (
          <p className="t-small text-danger">Der Link ließ sich nicht anlegen. Später erneut versuchen.</p>
        ) : (
          <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
            <Spinner /> {isPending ? 'Link wird angelegt' : 'Lädt'}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
